import {
  createContext,
  useContext,
  useState,
  type ReactNode,
  useEffect,
} from "react";

export interface EmailOption {
  name: string;
  email: string;
}

interface Email {
  to: EmailOption[];
  cc: EmailOption[];
  bcc: EmailOption[];
}

interface EmailContextType {
  emails: Email;
  setEmails: React.Dispatch<React.SetStateAction<Email>>;
  subject: string;
  setSubject: React.Dispatch<React.SetStateAction<string>>;
  showCC: boolean;
  setShowCC: React.Dispatch<React.SetStateAction<boolean>>;
  showBCC: boolean;
  setShowBCC: React.Dispatch<React.SetStateAction<boolean>>;
  attachments: File[];
  setAttachments: React.Dispatch<React.SetStateAction<File[]>>;
  isReply: boolean;
  setIsReply: React.Dispatch<React.SetStateAction<boolean>>;
  googleDriveConfig?: {
    /** The OAuth 2.0 Client ID obtained from the Google Cloud Console. */
    clientId: string;
    /** The API Key obtained from the Google Cloud Console. Ensure Google Picker API is enabled. */
    apiKey: string;
    /** Optional: List of discovery doc URLs for APIs used. Defaults to Drive v3. */
    discoveryDocs?: string[];
    /** Optional: Scopes required for the picker. Defaults to 'https://www.googleapis.com/auth/drive.readonly'. */
    scopes?: string;
  };
}

const EmailContext = createContext<EmailContextType | undefined>(undefined);

export const useEmailContext = () => {
  const context = useContext(EmailContext);
  if (!context) {
    throw new Error("useEmailContext must be used within an EmailProvider");
  }
  return context;
};

interface EmailProviderProps {
  children: ReactNode;
  googleDriveConfig?: {
    clientId: string;
    apiKey: string;
    discoveryDocs?: string[];
    scopes?: string;
  };
}

export const EmailProvider: React.FC<EmailProviderProps> = ({
  children,
  googleDriveConfig,
}) => {
  const [emails, setEmails] = useState<Email>({
    to: [],
    cc: [],
    bcc: [],
  });
  const [isReply, setIsReply] = useState(false);
  const [subject, setSubject] = useState("");
  const [showCC, setShowCC] = useState(false);
  const [showBCC, setShowBCC] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);

  // Log emails whenever they change
  useEffect(() => {
    console.log("EmailProvider emails updated:", emails);
  }, [emails]);

  return (
    <EmailContext.Provider
      value={{
        emails,
        setEmails,
        subject,
        setSubject,
        showCC,
        setShowCC,
        showBCC,
        setShowBCC,
        attachments,
        setAttachments,
        isReply,
        setIsReply,
        googleDriveConfig,
      }}>
      {children}
    </EmailContext.Provider>
  );
};
